import { useState, useEffect } from "react"
import { FaXTwitter } from "react-icons/fa6"
import groupBy from "lodash/groupBy"
import sortBy from "lodash/sortBy"
import find from "lodash/find"
import { format } from "date-fns"
import CrfImage from "lib/crfImage"
import {
    Box,
    CloseButton,
    Drawer,
    Flex,
    Heading,
    Icon,
    Link,
    Portal,
    Spinner,
    Text,
    useBreakpointValue,
    useDisclosure,
} from "@chakra-ui/react"

import { useGlobalState } from "providers/globalState"
import { TimezoneProvider } from "providers/timezone"
import { useConferenceEvents } from "services/emma/emma"
import { findEventById } from "src/util"
import { roomsHidden } from "lib/rooms"
import logger from "util/loggers/loggerBrowser"
import RenderLink from "components/agenda/renderLink"
import FormattedTimeString from "components/agenda/formattedTimeString"
import FormattedDateString from "components/agenda/formattedDateString"
import Session from "components/agenda/session"

export default function FacultyProfile({ profile, context = "full" }) {
    const { site } = useGlobalState()
    const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md" })
    const [structuredEvents, setStructuredEvents] = useState([])
    const [sessionCount, setSessionCount] = useState(0)
    const [selectedSession, setSelectedSession] = useState()
    const { open, onOpen, onClose } = useDisclosure()

    // get root events because we need to recreate the context (parents) of assignments
    const agendaEventIds = [...new Set(profile.assignments.map(assignment => assignment.event?.root_id))]
    const { data: confEvents } = useConferenceEvents(
        site.meeting.emmaMeetingId,
        { filter_ids: agendaEventIds },
        { queryKey: ["confEvents", profile.id] },
    )

    useEffect(() => {
        if (!confEvents) return

        // suppress moderator links in the session drawer
        if (context === "full") {
            site.suppressModeratorLinks = true
        }

        let sections = [],
            _sessionCount = 0

        // sort thru assignments and find the section it belongs to (which could be itself)
        profile.assignments.forEach(assignment => {
            // filter out private events
            if (assignment.event.status === "Private") return false

            const _section = findSection(assignment.event, confEvents)

            if (!_section) {
                if (assignment.event.depth !== 0) {
                    logger.info(`fac profile section not found (fac.id: ${profile.id}, assign.id: ${assignment.id})`)
                }
                return false
            }

            _sessionCount++

            const existingSection = sections.find(section => section.id === _section.id)

            if (!existingSection) {
                _section.presentationAssignments = []
                sections.push(_section)
            }

            // > 1 means presentations and sub-presentations will be rendered the same
            if (assignment.event.depth > 1) {
                const evt = findEventById(assignment.event.id, confEvents)
                if (evt) {
                    _section.presentationAssignments.push(evt)
                }
            }
        })

        setSessionCount(_sessionCount)

        function findSection(event, confEvents) {
            // ignore event level assignments
            if (event.depth === 0) return false

            if (event.depth === 1) return findEventById(event.id, confEvents)

            const parentEvent = findEventById(event.parent_id, confEvents)

            if (!parentEvent) return false

            return findSection(parentEvent, confEvents)
        }

        // group by parent session
        const sectionsGroupedBySession = groupBy(sections, section => section.parent_id)

        const sectionsGroupedBySessionSorted = sortBy(sectionsGroupedBySession, session => session[0].datetime_start)

        // set up sessions
        const sessions = sectionsGroupedBySessionSorted.map(sections => {
            const sessionId = sections[0].parent_id
            const session = findEventById(parseInt(sessionId, 10), confEvents)
            return {
                dayLabel: session.params?.on_demand
                    ? "On Demand"
                    : format(new Date(sections[0].datetime_start), "EEEE, MMMM d"),
                date: sections[0].datetime_start,
                sections: sections,
                channelSlug: getChannelSlug(session.session_type_id),
                room:
                    session.venue?.breadcrumb.split(" | ").reverse().join(", ") ||
                    "TBD" + (session.venue?.name && ", " + session.venue.name),
                ...session,
            }
        })

        // then group sessions by day
        const sessionsGroupedByDay = groupBy(sessions, session => session.dayLabel)

        // set up days
        let days = Object.keys(sessionsGroupedByDay).map(dayLabel => {
            const sessionGroup = sessionsGroupedByDay[dayLabel]
            return {
                dayLabel: dayLabel,
                date: sessionGroup[0].date,
                sessionGroup: sessionGroup,
            }
        })

        // order days & move the On Demand 'day' to the end
        days.sort((dayA, dayB) => {
            if (dayA.dayLabel === "On Demand") return 1
            return dayA.date < dayB.date ? -1 : 1
        })

        setStructuredEvents(days)
    }, [confEvents, profile.assignments, profile.id])

    function getChannelSlug(sessionTypeId) {
        const channel = find(site.channels, channel => {
            return parseInt(channel.sessionTypeID, 10) === sessionTypeId
        })

        return channel ? channel.slug : false
    }

    const ConditionalLink = function ({ session, index }) {
        return (
            <Text
                className="facultyProfile__sessionGroupTitle"
                mt={index ? 6 : 3}
                fontSize="1.0625em"
                fontWeight={600}
                textDecoration={context === "drawer" ? null : "underline"}
                cursor={context === "drawer" ? null : "pointer"}
                onClick={() => {
                    if (context === "drawer") return
                    setSelectedSession(session)
                    onOpen()
                }}
            >
                {session.title}
            </Text>
        )
    }

    return (
        <TimezoneProvider>
            <Flex
                className="facultyProfile"
                m="50px 0 20px"
                direction={isMobile || context === "drawer" ? "column" : "row"}
            >
                <Box className="facultyProfile__photo" mr={5} mb={5}>
                    <CrfImage
                        src={profile.image_url}
                        fallbackSrc="/person.png"
                        alt={profile.full_name}
                        width={0}
                        height={0}
                        sizes="200px"
                        style={{ width: "100%", maxWidth: 200, height: "auto" }}
                    />
                </Box>
                <Box className="facultyProfile__body" mb={12} fontSize={site.isWebView ? "125%" : null}>
                    <Heading className="facultyProfile__name" as="h1" m={0}>
                        {profile.full_name + (profile.degree ? ", " + profile.degree.split(",").join(", ") : "")}
                    </Heading>
                    {profile.social_twitter ? (
                        <Box className="facultyProfile__twitter twitter-link" m={0}>
                            <Link
                                href={"https://twitter.com/" + profile.social_twitter}
                                target="_blank"
                                color="color4"
                                fontSize="0.875em"
                                _hover={{ textDecoration: "none" }}
                            >
                                <Box display="inline" pr={1}>
                                    <Icon as={FaXTwitter} w="18px" h="18px" fill="color4" verticalAlign="middle" />
                                </Box>
                                @{profile.social_twitter}
                            </Link>
                        </Box>
                    ) : null}
                    {profile.medical_institution && (
                        <Text className="facultyProfile__institution" mt={2}>
                            {profile.medical_institution}
                        </Text>
                    )}

                    <Box
                        mt={6}
                        className="facultyProfile__description body"
                        dangerouslySetInnerHTML={{ __html: profile.description }}
                    />

                    <Heading className="facultyProfile__sessionsHeader" as="h2" fontSize="24px" mt={8} color="color1">
                        {"Session" + (sessionCount === 1 ? "" : "s")}
                    </Heading>

                    {confEvents ? (
                        structuredEvents.length ? (
                            <Box className="facultyProfile__sessionsWrapper" maxW="700px">
                                {structuredEvents.map((day, i) => {
                                    return (
                                        <Box className="facultyProfile__day" key={i}>
                                            <Heading
                                                className="facultyProfile__dayHeader"
                                                as="h3"
                                                fontSize="1.25em"
                                                color="color4"
                                                mt={8}
                                                mb={0}
                                            >
                                                <FormattedDateString date={day.date} format="EEEE, MMMM d" />
                                            </Heading>
                                            {day.sessionGroup.map((sessionGroup, j) => {
                                                return (
                                                    <Box
                                                        className="facultyProfile__sessionGroup"
                                                        key={profile.id + sessionGroup.id}
                                                    >
                                                        <ConditionalLink session={sessionGroup} index={j} />
                                                        {!roomsHidden(site) && (
                                                            <Text
                                                                className="facultyProfile__sessionGroupRoom"
                                                                m={0}
                                                                fontWeight={400}
                                                                opacity={0.8}
                                                            >
                                                                {"Room: " + sessionGroup.room}
                                                            </Text>
                                                        )}
                                                        <Sections
                                                            profileId={profile.id}
                                                            sections={sessionGroup.sections}
                                                            channelSlug={sessionGroup.channelSlug}
                                                            site={site}
                                                        />
                                                    </Box>
                                                )
                                            })}
                                        </Box>
                                    )
                                })}
                            </Box>
                        ) : (
                            <Text fontStyle="italic">No sessions found.</Text>
                        )
                    ) : (
                        <Box mt={2}>
                            <Spinner thickness="3px" speed="0.5s" color="#ddd" emptyColor="transparent" size="xs" />
                            <Text as="span" pl={2} fontStyle="italic">
                                Looking up sessions...
                            </Text>
                        </Box>
                    )}
                </Box>
            </Flex>
            <Drawer.Root open={open} onOpenChange={({ open }) => !open && onClose()} placement="end" size="lg">
                <Portal>
                    <Drawer.Backdrop />
                    <Drawer.Positioner>
                        <Drawer.Content>
                            <Drawer.Body color="#333">
                                {selectedSession && (
                                    <Box>
                                        <Heading as="h3" color="color1" fontSize="1.75em" lineHeight={1.2} px={4}>
                                            {selectedSession.title}
                                        </Heading>
                                        <Session bgColor="#fff" color="color1" {...selectedSession} />
                                    </Box>
                                )}
                            </Drawer.Body>
                            <Drawer.CloseTrigger asChild>
                                <CloseButton size="sm" />
                            </Drawer.CloseTrigger>
                        </Drawer.Content>
                    </Drawer.Positioner>
                </Portal>
            </Drawer.Root>
        </TimezoneProvider>
    )
}

function Sections({ profileId, sections, channelSlug, site }) {
    const ConditionalLink = function ({ event, children }) {
        if (!site?.meeting.isLive || event.status === "Private") return children

        return (
            <RenderLink event={event} channelSlug={channelSlug} site={site}>
                {children}
            </RenderLink>
        )
    }

    return sections.map(section => {
        return (
            <Box className="facultyProfile__section" key={profileId + section.id} mt={2}>
                <Flex my={1} fontWeight={600}>
                    <Text mt={0} w="5em" flexShrink={0} flexGrow={0}>
                        <FormattedTimeString event={section} />
                    </Text>
                    <Box>
                        <ConditionalLink event={section}>
                            <Text as="span">{section.title}</Text>
                        </ConditionalLink>
                    </Box>
                </Flex>
                <Presentations
                    key={profileId + section.id}
                    profileId={profileId}
                    presentations={section.presentationAssignments}
                    ConditionalLink={ConditionalLink}
                />
            </Box>
        )
    })
}

function Presentations({ profileId, presentations, ConditionalLink }) {
    return presentations.map(presentation => (
        <Flex className="facultyProfile__presentation" key={profileId + presentation.id} mt={0} mb={1}>
            <Text w="5em" mt={0} flexShrink={0} flexGrow={0}>
                <FormattedTimeString event={presentation} />
            </Text>
            <Box opacity={0.8}>
                <ConditionalLink event={presentation}>
                    <Text as="span" fontSize=".9375em">
                        {presentation.title}
                    </Text>
                </ConditionalLink>
            </Box>
        </Flex>
    ))
}
